/**
 * Cockpit CFO Finance Director — vue 1-écran (PM Spec 10/05/2026)
 * Inspiration : Stripe Atlas, Pylon, Linear Insights.
 * Charte : Baobab #16A34A + zinc + blanc. Densité info maximale.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import FinanceAuditPage from './FinanceAuditPage';
import TreasureGlobalePage from './TreasureGlobalePage';
import FneComplianceWidget from '../../components/FneComplianceWidget';
import CashForecastTab from '../../components/finance/CashForecastTab';
import CashflowTab from '../../components/finance/CashflowTab';
import ClientMarginTab from '../../components/finance/ClientMarginTab';
import HiddenTaxTab from '../../components/finance/HiddenTaxTab';
import RegimeThresholdBanner from '../../components/finance/RegimeThresholdBanner';
import { useAuth } from '../../contexts/AuthContext';
import {
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, Clock, Users,
  Target, Sparkles, RefreshCw, Wallet, Zap, ArrowUpRight, ArrowDownRight,
  CheckCircle2, AlertCircle, Settings, FileX, Activity, PieChart as PieIcon,
  ChevronRight, BarChart3, FileText, Mail, Beaker, Brain, Crown, Coins,
  AlertOctagon, Loader2, Send, FileSpreadsheet, X, ShieldAlert, BellRing, Landmark,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Cell, ReferenceLine, Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import { RequirePermission } from '../../components/permissions/RequirePermission';
import { PERMS } from '../../utils/permissions';
import { sanitizeHtml } from '../../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ================== HELPERS ================== */
const fmt = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(1)} Md`;
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)} M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(0)} k`;
  return Math.round(v).toLocaleString('fr-FR');
};
const fxof = (v) => `${fmt(v)} XOF`;
const fpct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

/* ================== KPI CARD ================== */
const KpiCard = ({ label, value, sub, delta, deltaLabel, icon: Icon, accent = 'zinc', testId, alert, onClick }) => {
  const accentMap = {
    emerald: { text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    rose: { text: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200' },
    amber: { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    blue: { text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    violet: { text: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
    zinc: { text: 'text-zinc-600', bg: 'bg-zinc-100', border: 'border-zinc-200' },
  };
  const a = accentMap[accent] || accentMap.zinc;
  const isPositive = delta != null && delta >= 0;
  const DeltaIcon = isPositive ? ArrowUpRight : ArrowDownRight;
  const deltaCls = isPositive ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50';

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`relative bg-white border ${alert ? 'border-rose-300 ring-1 ring-rose-100' : 'border-zinc-200'} rounded-xl p-4 text-left transition-all duration-200 hover:border-zinc-300 hover:shadow-sm ${onClick ? 'cursor-pointer focus-visible:ring-2 focus-visible:ring-[#16A34A] focus-visible:outline-none' : 'cursor-default'}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 leading-tight">{label}</span>
        {Icon && (
          <div className={`h-7 w-7 rounded-lg ${a.bg} flex items-center justify-center shrink-0`}>
            <Icon className={`h-3.5 w-3.5 ${a.text}`} />
          </div>
        )}
      </div>
      <div className={`text-2xl font-bold tracking-tight tabular-nums ${a.text}`} title={value}>{value}</div>
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        {sub && <span className="text-xs text-zinc-500">{sub}</span>}
        {delta != null && (
          <span className={`inline-flex items-center gap-0.5 ${deltaCls} text-[10px] font-bold px-1.5 py-0.5 rounded`}>
            <DeltaIcon className="h-2.5 w-2.5" />
            {fpct(delta)}
            {deltaLabel && <span className="font-medium ml-0.5 opacity-75">{deltaLabel}</span>}
          </span>
        )}
      </div>
      {alert && (
        <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
      )}
    </button>
  );
};

/* ================== ALERTS BANNER ================== */
const AlertCard = ({ alert }) => {
  const sevMap = {
    high: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-900', icon: AlertTriangle, iconColor: 'text-rose-600' },
    medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: AlertCircle, iconColor: 'text-amber-600' },
    low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', icon: Activity, iconColor: 'text-blue-600' },
  };
  const s = sevMap[alert.severity] || sevMap.low;
  const Icon = s.icon;
  return (
    <div className={`flex items-start gap-2.5 ${s.bg} border ${s.border} rounded-lg px-3 py-2.5`} data-testid={`alert-${alert.type}`}>
      <Icon className={`h-4 w-4 ${s.iconColor} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${s.text} leading-snug`}>{alert.message}</p>
      </div>
    </div>
  );
};

/* ================== BUDGET DIALOG ================== */
const BudgetDialog = ({ open, onClose, onSave, budget }) => {
  const [target, setTarget] = useState(budget?.monthly_revenue_target || 0);
  const [marginPct, setMarginPct] = useState(budget?.monthly_margin_target_pct || 20);

  useEffect(() => {
    setTarget(budget?.monthly_revenue_target || 0);
    setMarginPct(budget?.monthly_margin_target_pct || 20);
  }, [budget]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="budget-dialog">
        <DialogHeader>
          <DialogTitle>Configurer l'objectif budgétaire</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">CA mensuel cible (XOF)</label>
            <Input type="number" value={target} onChange={(e) => setTarget(Number(e.target.value))} className="tabular-nums" data-testid="budget-target-input" />
            <p className="text-xs text-zinc-500 mt-1">{fxof(target)} / mois soit {fxof(target * 12)} / an</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Marge brute cible (%)</label>
            <Input type="number" value={marginPct} onChange={(e) => setMarginPct(Number(e.target.value))} step="0.5" className="tabular-nums" data-testid="budget-margin-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button onClick={() => onSave({ monthly_revenue_target: target, monthly_margin_target_pct: marginPct })}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="budget-save-btn">
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ================== PROFITABILITY TAB ================== */
const RATING_META = {
  champion: { label: '🏆 Champion', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  star:     { label: '⭐ Star',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cash_cow: { label: '💰 Cash-cow',  cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  risk:     { label: '⚠️ Risk',      cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  problem:  { label: '🚨 Problem',   cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const ProfitabilityTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/finance/cockpit/customer-profitability?months=12`,
          {});
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="text-center py-12 text-zinc-500 text-sm"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Calcul de la profitabilité client...</div>;
  if (!data?.clients?.length) return <div className="text-center py-12 text-zinc-500 text-sm">Aucune donnée client sur les 12 derniers mois.</div>;

  const counts = data.clients.reduce((acc, c) => { acc[c.rating] = (acc[c.rating] || 0) + 1; return acc; }, {});
  return (
    <div className="space-y-4" data-testid="tab-profitability">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(RATING_META).map(([k, m]) => (
          <div key={k} className={`border rounded-lg px-3 py-2 ${m.cls}`} data-testid={`rating-count-${k}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-75">{m.label}</div>
            <div className="text-xl font-bold tabular-nums">{counts[k] || 0}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50/50 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Profitabilité par client · {data.clients.length} clients · 12 mois</span>
          <Badge variant="outline" className="text-[10px]">Tri par CA</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="profitability-table">
            <thead>
              <tr className="border-b border-zinc-100 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                <th className="text-left px-4 py-2.5">#</th>
                <th className="text-left px-3 py-2.5">Client</th>
                <th className="text-right px-3 py-2.5">CA 12m</th>
                <th className="text-right px-3 py-2.5">Coût T.</th>
                <th className="text-right px-3 py-2.5">Marge</th>
                <th className="text-right px-3 py-2.5">Marge %</th>
                <th className="text-right px-3 py-2.5">DSO</th>
                <th className="text-right px-3 py-2.5">Encours</th>
                <th className="text-right px-3 py-2.5">Ops</th>
                <th className="text-center px-3 py-2.5">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.slice(0, 30).map((c, i) => {
                const m = RATING_META[c.rating] || RATING_META.risk;
                return (
                  <tr key={c.name} className="border-b border-zinc-50 hover:bg-zinc-50/50" data-testid={`prof-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-xs text-zinc-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-zinc-900 truncate max-w-[180px]">{c.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(c.revenue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{fmt(c.carrierCost)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${c.grossMargin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(c.grossMargin)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${c.grossMarginPct >= 20 ? 'text-emerald-700' : c.grossMarginPct >= 10 ? 'text-amber-700' : 'text-rose-700'}`}>{c.grossMarginPct.toFixed(1)}%</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${c.dso > 60 ? 'text-rose-700' : c.dso > 45 ? 'text-amber-700' : 'text-zinc-600'}`}>{c.dso}j</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{fmt(c.outstanding)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{c.opsCount}</td>
                    <td className="px-3 py-2 text-center"><Badge variant="outline" className={`${m.cls} text-[10px]`}>{m.label}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ================== DPO/CCC TAB ================== */
const DpoCccTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/finance/cockpit/dpo-ccc`,
          {});
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="text-center py-12 text-zinc-500 text-sm"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Calcul DPO/CCC...</div>;
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="tab-dpo-ccc">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard testId="kpi-dpo" label="DPO" value={`${data.dpo} j`} sub="Days Payable Outstanding" icon={Coins} accent="blue" />
        <KpiCard testId="kpi-dso-2" label="DSO" value={`${data.dso} j`} sub="Days Sales Outstanding" icon={Clock} accent={data.dso <= 45 ? 'emerald' : data.dso <= 60 ? 'amber' : 'rose'} />
        <KpiCard testId="kpi-ccc" label="Cash Conversion Cycle" value={`${data.ccc} j`} sub="DSO − DPO (services: DIO=0)" icon={Activity} accent={data.ccc <= 30 ? 'emerald' : data.ccc <= 60 ? 'amber' : 'rose'} />
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl p-4 text-sm space-y-2">
        <h3 className="font-semibold text-zinc-950 mb-2">📖 Lecture des indicateurs</h3>
        <ul className="space-y-1.5 text-zinc-700 list-disc list-inside">
          <li><strong>DPO {data.dpo}j</strong> — vous mettez en moyenne {data.dpo} jours à payer vos transporteurs (encours fournisseurs {fmt(data.totalCarrierOutstanding)} sur coût total {fmt(data.totalCarrierCost)}).</li>
          <li><strong>CCC {data.ccc}j</strong> — durée entre le moment où vous payez vos transporteurs et celui où votre client vous paie. {data.ccc > 0 ? `Vous financez ${data.ccc} jours d'activité — besoin de trésorerie corrélé.` : 'Excellent : vos clients vous payent avant que vous ne payiez les transporteurs.'}</li>
          <li className="text-xs text-zinc-500">Hypothèse : DPO = encours / coût × 365. Affiner avec collection <code>payments</code> dédiée fournisseurs.</li>
        </ul>
      </div>
    </div>
  );
};

/* ================== STRESS TEST TAB ================== */
const StressTestTab = ({ topClient }) => {
  const [scenario, setScenario] = useState('top1');
  const [customClient, setCustomClient] = useState('');
  const [dropPct, setDropPct] = useState(20);
  const [dsoIncrease, setDsoIncrease] = useState(15);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const body = {};
      if (scenario === 'top1') body.loseTop1 = true;
      else if (scenario === 'client') body.clientToLose = customClient;
      else if (scenario === 'drop') body.revenueDropPct = Number(dropPct);
      else if (scenario === 'dso') body.dsoIncreaseDays = Number(dsoIncrease);
      const r = await axios.post(`${API}/admin/finance/cockpit/stress-test`, body,
        {});
      setResult(r.data);
    } catch (e) {
      toast({ title: 'Erreur stress test', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-stress-test">
      <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-950 flex items-center gap-2"><Beaker className="h-4 w-4 text-violet-600" /> Configurer le scénario</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button onClick={() => setScenario('top1')} className={`text-left p-3 rounded-lg border-2 transition ${scenario === 'top1' ? 'border-rose-300 bg-rose-50' : 'border-zinc-200 hover:border-zinc-300'}`} data-testid="scenario-top1">
            <div className="text-xs font-bold uppercase tracking-wider text-rose-700">Perte Top-1</div>
            <div className="text-sm text-zinc-700 mt-1">{topClient ? `Perdre "${topClient}"` : 'Perdre le client #1'}</div>
          </button>
          <button onClick={() => setScenario('drop')} className={`text-left p-3 rounded-lg border-2 transition ${scenario === 'drop' ? 'border-amber-300 bg-amber-50' : 'border-zinc-200 hover:border-zinc-300'}`} data-testid="scenario-drop">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Baisse CA globale</div>
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" value={dropPct} onChange={(e) => setDropPct(e.target.value)} onClick={(e) => e.stopPropagation()} className="h-7 w-16 text-sm" />
              <span className="text-sm text-zinc-700">% de baisse</span>
            </div>
          </button>
          <button onClick={() => setScenario('dso')} className={`text-left p-3 rounded-lg border-2 transition ${scenario === 'dso' ? 'border-orange-300 bg-orange-50' : 'border-zinc-200 hover:border-zinc-300'}`} data-testid="scenario-dso">
            <div className="text-xs font-bold uppercase tracking-wider text-orange-700">DSO en hausse</div>
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" value={dsoIncrease} onChange={(e) => setDsoIncrease(e.target.value)} onClick={(e) => e.stopPropagation()} className="h-7 w-16 text-sm" />
              <span className="text-sm text-zinc-700">jours supplémentaires</span>
            </div>
          </button>
          <button onClick={() => setScenario('client')} className={`text-left p-3 rounded-lg border-2 transition ${scenario === 'client' ? 'border-violet-300 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'}`} data-testid="scenario-client">
            <div className="text-xs font-bold uppercase tracking-wider text-violet-700">Perte client spécifique</div>
            <Input value={customClient} onChange={(e) => setCustomClient(e.target.value)} onClick={(e) => e.stopPropagation()} placeholder="Nom du client..." className="h-7 mt-1 text-sm" />
          </button>
        </div>
        <Button onClick={run} disabled={running} className="bg-violet-600 hover:bg-violet-700 text-white w-full md:w-auto" data-testid="run-stress-btn">
          {running ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Calcul...</> : <><Beaker className="h-4 w-4 mr-1.5" />Lancer le test</>}
        </Button>
      </div>
      {result && (
        <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="stress-result">
          <h3 className="text-sm font-semibold text-zinc-950 mb-3">Impact simulé</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'CA 12 mois', before: result.before.revenue, after: result.after.revenue, isXof: true },
              { label: 'Marge brute', before: result.before.grossMargin, after: result.after.grossMargin, isXof: true },
              { label: 'DSO', before: result.before.dso, after: result.after.dso, isXof: false, suffix: 'j' },
            ].map((row) => {
              const delta = row.after - row.before;
              const pct = row.before !== 0 ? (delta / row.before * 100) : 0;
              return (
                <div key={row.label} className="border border-zinc-200 rounded-lg p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{row.label}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-xs text-zinc-400 line-through tabular-nums">{row.isXof ? fxof(row.before) : `${row.before}${row.suffix || ''}`}</span>
                  </div>
                  <div className={`text-xl font-bold tabular-nums ${delta < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{row.isXof ? fxof(row.after) : `${row.after}${row.suffix || ''}`}</div>
                  <div className={`text-xs font-bold tabular-nums mt-0.5 ${delta < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{delta >= 0 ? '+' : ''}{row.isXof ? fxof(delta) : `${delta}${row.suffix || ''}`} ({pct >= 0 ? '+' : ''}{pct.toFixed(1)}%)</div>
                </div>
              );
            })}
          </div>
          {result.scenario.affectedClient && (
            <p className="text-xs text-zinc-500 mt-3">Client simulé : <strong className="text-zinc-700">{result.scenario.affectedClient}</strong></p>
          )}
        </div>
      )}
    </div>
  );
};

/* ================== AI INSIGHTS ================== */
const AiInsightsButton = () => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);

  const generate = async () => {
    setOpen(true);
    setLoading(true);
    setReport(null);
    try {
      const r = await axios.post(`${API}/admin/finance/cockpit/insights-ai`, {}, {});
      setReport(r.data || {});
      if (r.data?.error) toast({ title: 'IA partiellement OK', description: r.data.error, variant: 'destructive' });
    } catch (e) {
      toast({ title: 'IA indisponible', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const meta = report?.meta;
  const providerLabel = meta?.provider === 'openai'
    ? `OpenAI ${meta.model || ''}`
    : meta?.provider === 'anthropic'
      ? `Claude ${meta.model?.replace('claude-4-sonnet-20250514', 'Sonnet 4.5') || ''}`
      : 'IA';
  const sourceLabel = {
    'env:OCR_OPENAI_API_KEY': 'Clé OCR .env',
    'db:ocr_config': 'Clé OCR utilisateur',
    'emergent_llm_key': 'Emergent LLM',
    'emergent_llm_key_fallback': 'Emergent LLM (fallback)',
  }[meta?.source] || meta?.source || '';

  const arrow = (d) => d === 'up' ? '↗️' : d === 'down' ? '↘️' : '➡️';
  const dirColor = (d, label) => {
    // Heuristique : pour DSO/Concentration, "up" = mauvais
    const isBadWhenUp = /dso|concentration|retard|risque/i.test(label || '');
    if (d === 'stable') return 'text-zinc-600';
    if (isBadWhenUp) return d === 'up' ? 'text-rose-600' : 'text-emerald-600';
    return d === 'up' ? 'text-emerald-600' : 'text-rose-600';
  };
  const levelBadge = {
    critical: 'bg-rose-100 text-rose-800 border-rose-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    medium: 'bg-amber-100 text-amber-800 border-amber-300',
    low: 'bg-blue-100 text-blue-800 border-blue-300',
  };
  const ownerBadge = {
    CFO: 'bg-violet-100 text-violet-800',
    Commercial: 'bg-blue-100 text-blue-800',
    Ops: 'bg-emerald-100 text-emerald-800',
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={generate} className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="ai-insights-btn">
        <Brain className="h-4 w-4 mr-1.5" /> Analyse IA approfondie
      </Button>
      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-4xl" data-testid="ai-insights-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-violet-600" />
                Rapport CFO IA — Analyse approfondie
              </span>
              {meta?.provider && !loading && (
                <span className="text-[10px] font-normal text-zinc-500 inline-flex items-center gap-1.5 mr-6">
                  <span className={`px-2 py-0.5 rounded ${meta.provider === 'openai' ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800'}`}>
                    {providerLabel}
                  </span>
                  <span className="text-zinc-400">· {sourceLabel}</span>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="h-10 w-10 mx-auto animate-spin text-violet-500" />
              <p className="text-sm text-zinc-600 mt-3 font-medium">Analyse approfondie en cours...</p>
              <p className="text-xs text-zinc-400 mt-1">15-30 secondes — l'IA examine 12 mois de données financières</p>
            </div>
          ) : !report ? null : report.error && !report.synthesis ? (
            <div className="py-8">
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                <div className="font-semibold mb-1">❌ IA indisponible</div>
                <div className="text-xs">{report.error}</div>
                <div className="text-xs mt-2 text-rose-600">
                  💡 Configurez votre clé OpenAI dans <strong>Paramètres → OCR</strong> (la même clé sera réutilisée ici).
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">

              {/* SYNTHÈSE */}
              {report.synthesis && (
                <div className="rounded-lg border-l-4 border-violet-500 bg-violet-50 p-4" data-testid="ai-synthesis">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 mb-1.5">🎯 Synthèse exécutive</div>
                  <p className="text-sm text-zinc-900 leading-relaxed">{report.synthesis}</p>
                </div>
              )}

              {/* TENDANCES */}
              {(report.trends || []).length > 0 && (
                <div data-testid="ai-trends">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-2 px-1">📈 Tendances</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {report.trends.map((t, i) => (
                      <div key={i} className="rounded-md border border-zinc-200 bg-white p-3">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-xs font-semibold text-zinc-700">{t.label}</span>
                          <span className={`text-xs font-bold whitespace-nowrap ${dirColor(t.direction, t.label)}`}>
                            {arrow(t.direction)} {t.magnitude}
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-600 mt-1 leading-snug">{t.narrative}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RECOMMANDATIONS */}
              {(report.recommendations || []).length > 0 && (
                <div data-testid="ai-recommendations">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-2 px-1">💡 Recommandations prioritaires</div>
                  <div className="space-y-2">
                    {report.recommendations.map((r, i) => (
                      <div key={i} className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">{r.priority}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-zinc-900">{r.action}</p>
                              <p className="text-[11px] text-zinc-600 mt-0.5">{r.rationale}</p>
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {r.estimatedImpact && (
                                  <span className="text-[10px] font-medium text-emerald-700 bg-white border border-emerald-200 px-1.5 py-0.5 rounded">
                                    💰 {r.estimatedImpact}
                                  </span>
                                )}
                                {r.owner && (
                                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${ownerBadge[r.owner] || 'bg-zinc-100 text-zinc-700'}`}>
                                    {r.owner}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RISQUES */}
              {(report.risks || []).length > 0 && (
                <div data-testid="ai-risks">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 mb-2 px-1">⚠️ Risques détectés</div>
                  <div className="space-y-2">
                    {report.risks.map((rk, i) => (
                      <div key={i} className={`rounded-md border p-3 ${levelBadge[rk.level] || levelBadge.medium}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-xs font-bold uppercase tracking-wider">{rk.category}</span>
                              <span className="text-[10px] opacity-70">· Score {rk.score}/5</span>
                            </div>
                            <p className="text-xs leading-snug">{rk.narrative}</p>
                            {rk.mitigation && (
                              <p className="text-[11px] mt-1.5 font-medium opacity-90">
                                🛡️ Mitigation : <span className="font-normal">{rk.mitigation}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PROJECTION 90j */}
              {report.projection90d?.narrative && (
                <div className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 p-4" data-testid="ai-projection">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-700">🔮 Projection 90 jours</span>
                    {report.projection90d.confidence && (
                      <span className="text-[10px] text-blue-700">
                        Confiance : {report.projection90d.confidence}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-900 leading-relaxed">{report.projection90d.narrative}</p>
                  {(report.projection90d.cashIn || report.projection90d.cashOutCritical) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                      {report.projection90d.cashIn && (
                        <div className="rounded bg-white/60 p-2">
                          <div className="text-[10px] uppercase font-semibold text-emerald-700">Encaissements attendus</div>
                          <div className="text-sm font-bold text-zinc-900">{report.projection90d.cashIn}</div>
                        </div>
                      )}
                      {report.projection90d.cashOutCritical && (
                        <div className="rounded bg-white/60 p-2">
                          <div className="text-[10px] uppercase font-semibold text-rose-700">Sorties critiques</div>
                          <div className="text-sm font-bold text-zinc-900">{report.projection90d.cashOutCritical}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Footer meta */}
              {meta?.generated_at && (
                <div className="text-[10px] text-zinc-400 text-center pt-2 border-t">
                  Généré le {new Date(meta.generated_at).toLocaleString('fr-FR')} via {providerLabel}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};


/* ================== ÉTATS FINANCIERS DIALOG (CFO 360°) ================== */
const PERIOD_PRESETS = [
  { id: 'current_month', label: 'Mois en cours' },
  { id: 'last_month', label: 'Mois précédent' },
  { id: 'current_quarter', label: 'Trimestre en cours' },
  { id: 'ytd', label: 'YTD (depuis 1er janv.)' },
  { id: 'rolling_12m', label: '12 mois glissants' },
  { id: 'last_year', label: 'Année précédente' },
  { id: 'custom', label: 'Personnalisé' },
];

const computePresetDates = (presetId) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  switch (presetId) {
    case 'current_month':
      return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };
    case 'last_month':
      return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
    case 'current_quarter': {
      const qStart = Math.floor(m / 3) * 3;
      return { start: iso(new Date(y, qStart, 1)), end: iso(new Date(y, qStart + 3, 0)) };
    }
    case 'ytd':
      return { start: iso(new Date(y, 0, 1)), end: iso(now) };
    case 'rolling_12m': {
      const start = new Date(now); start.setFullYear(start.getFullYear() - 1);
      return { start: iso(start), end: iso(now) };
    }
    case 'last_year':
      return { start: iso(new Date(y - 1, 0, 1)), end: iso(new Date(y - 1, 11, 31)) };
    default:
      return null;
  }
};

const EtatsFinanciersDialog = ({ open, onClose }) => {
  const [preset, setPreset] = useState('ytd');
  const initial = computePresetDates('ytd');
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [compare, setCompare] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (preset !== 'custom') {
      const d = computePresetDates(preset);
      if (d) { setStartDate(d.start); setEndDate(d.end); }
    }
  }, [preset]);

  useEffect(() => {
    if (!open || !startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/finance/etats-financiers`, {
      params: { startDate, endDate, compare }
    }).then(r => { if (!cancelled) setPreview(r.data); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, startDate, endDate, compare]);

  const handleDownload = async (format = 'pdf') => {
    setDownloading(true);
    try {
      const path = format === 'excel'
        ? `${API}/admin/finance/etats-financiers/excel`
        : `${API}/admin/finance/etats-financiers/pdf`;
      const r = await axios.get(path, {
        params: { startDate, endDate, compare },
        responseType: 'blob',
      });
      const mime = format === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `etats-financiers-${startDate}-${endDate}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: `Téléchargé (${ext.toUpperCase()})`, description: `${startDate} → ${endDate}` });
    } catch (e) {
      toast({ title: 'Erreur de génération', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const [drillType, setDrillType] = useState(null); // 'top-clients' | 'outstanding' | 'operations'
  const [drillBucket, setDrillBucket] = useState(null);
  const openDrill = (type, bucket = null) => { setDrillType(type); setDrillBucket(bucket); };

  const fmt = (v) => {
    if (v == null) return '—';
    const abs = Math.abs(v);
    if (abs >= 1e9) return `${(v/1e9).toFixed(2)} Md`;
    if (abs >= 1e6) return `${(v/1e6).toFixed(2)} M`;
    if (abs >= 1e3) return `${(v/1e3).toFixed(0)} k`;
    return `${Math.round(v)}`;
  };
  const cur = preview?.current;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="etats-financiers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#16A34A]" />
            États Financiers — CFO 360°
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="text-xs font-semibold text-zinc-700 mb-2 block">Période d'analyse</Label>
            <div className="flex flex-wrap gap-2" data-testid="period-presets">
              {PERIOD_PRESETS.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  data-testid={`preset-${p.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    preset === p.id
                      ? 'bg-[#16A34A] text-white border-[#16A34A]'
                      : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-600">Du</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => { setStartDate(e.target.value); setPreset('custom'); }}
                data-testid="start-date-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Au</Label>
              <Input
                type="date"
                value={endDate}
                onChange={e => { setEndDate(e.target.value); setPreset('custom'); }}
                data-testid="end-date-input"
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={compare} onCheckedChange={setCompare} data-testid="compare-toggle" />
            <Label className="text-sm text-zinc-700 cursor-pointer" onClick={() => setCompare(!compare)}>
              Comparer à la période précédente (auto-symétrique)
            </Label>
          </div>

          <div className="border border-zinc-200 rounded-lg p-4 bg-zinc-50" data-testid="etats-preview">
            <div className="text-xs font-semibold text-zinc-600 mb-3 uppercase tracking-wider">Aperçu des chiffres</div>
            {loading && <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calcul en cours...</div>}
            {!loading && !cur && <div className="text-sm text-zinc-400">Aucune donnée sur cette période.</div>}
            {!loading && cur && (
              <div className="grid grid-cols-3 gap-3 text-xs">
                <button onClick={() => openDrill('top-clients')} className="text-left hover:bg-white rounded p-1 transition cursor-pointer" data-testid="drill-revenue">
                  <div className="text-zinc-500">CA total <span className="text-[9px] text-zinc-400">(détails)</span></div>
                  <div className="text-base font-bold text-zinc-900 tabular-nums">{fmt(cur.compteResultat.revenueTotal)} XOF</div>
                  {preview.deltas?.revenueTotal != null && (
                    <div className={`text-[10px] font-semibold ${preview.deltas.revenueTotal >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {preview.deltas.revenueTotal >= 0 ? '▲' : '▼'} {Math.abs(preview.deltas.revenueTotal).toFixed(1)}%
                    </div>
                  )}
                </button>
                <div className="p-1">
                  <div className="text-zinc-500">Marge brute</div>
                  <div className="text-base font-bold text-zinc-900 tabular-nums">{fmt(cur.compteResultat.margeBrute)} XOF</div>
                  <div className="text-[10px] text-zinc-500">{cur.compteResultat.margeBrutePct.toFixed(1)}%</div>
                </div>
                <div className="p-1">
                  <div className="text-zinc-500">Encaissé</div>
                  <div className="text-base font-bold text-zinc-900 tabular-nums">{fmt(cur.tresorerie.cashIn)} XOF</div>
                </div>
                <button onClick={() => openDrill('outstanding', 'all')} className="text-left hover:bg-white rounded p-1 transition cursor-pointer" data-testid="drill-outstanding">
                  <div className="text-zinc-500">Encours <span className="text-[9px] text-zinc-400">(aging)</span></div>
                  <div className={`text-base font-bold tabular-nums ${cur.tresorerie.outstandingTotal > 0 ? 'text-rose-600' : 'text-zinc-900'}`}>{fmt(cur.tresorerie.outstandingTotal)} XOF</div>
                  <div className="text-[10px] text-zinc-500">DSO {cur.tresorerie.dso.toFixed(0)}j</div>
                </button>
                <button onClick={() => openDrill('top-clients')} className="text-left hover:bg-white rounded p-1 transition cursor-pointer" data-testid="drill-top1">
                  <div className="text-zinc-500">Top-1 <span className="text-[9px] text-zinc-400">(détails)</span></div>
                  <div className="text-base font-bold text-zinc-900">{cur.concentration.top1Pct.toFixed(1)}%</div>
                  <div className="text-[10px] text-zinc-500 truncate">{cur.concentration.top1Name || '—'}</div>
                </button>
                <button onClick={() => openDrill('operations')} className="text-left hover:bg-white rounded p-1 transition cursor-pointer" data-testid="drill-operations">
                  <div className="text-zinc-500">Opérations <span className="text-[9px] text-zinc-400">(détails)</span></div>
                  <div className="text-base font-bold text-zinc-900 tabular-nums">{cur.activite.totalOps}</div>
                  <div className="text-[10px] text-zinc-500">Annul. {cur.activite.tauxAnnulation.toFixed(1)}%</div>
                </button>
              </div>
            )}
          </div>

          <div className="text-[11px] text-zinc-500 leading-relaxed">
            Le PDF contient <strong>5 pages</strong> A4. L'Excel contient <strong>5 onglets</strong> (Synthèse · Compte de Résultat · Trésorerie · Top Clients · Notes). Cliquez sur les KPIs ci-dessus pour ouvrir le détail.
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t flex-wrap">
          <Button variant="outline" onClick={onClose} data-testid="etats-cancel-btn">Annuler</Button>
          <Button
            variant="outline"
            onClick={() => handleDownload('excel')}
            disabled={downloading || loading || !cur}
            data-testid="etats-excel-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
          </Button>
          <Button
            onClick={() => handleDownload('pdf')}
            disabled={downloading || loading || !cur}
            className="bg-[#16A34A] hover:bg-[#15803d]"
            data-testid="etats-download-btn"
          >
            {downloading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération...</>
              : <><FileText className="h-4 w-4 mr-2" /> Télécharger le PDF</>}
          </Button>
        </div>

        {/* Drill-down drawer interne au dialog */}
        {drillType && (
          <DrillDownDrawer
            type={drillType}
            bucket={drillBucket}
            startDate={startDate}
            endDate={endDate}
            onClose={() => { setDrillType(null); setDrillBucket(null); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};


/* ================== DRILL-DOWN DRAWER ================== */
const DrillDownDrawer = ({ type, bucket, startDate, endDate, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBucket, setSelectedBucket] = useState(bucket || 'all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = type === 'outstanding'
      ? { endDate, bucket: selectedBucket }
      : { startDate, endDate, limit: 100 };
    axios.get(`${API}/admin/finance/etats-financiers/drilldown/${type}`, {
      params
    }).then(r => { if (!cancelled) setItems(r.data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [type, selectedBucket, startDate, endDate]);

  const exportCsv = async () => {
    const params = type === 'outstanding'
      ? { endDate, bucket: selectedBucket, format: 'csv' }
      : { startDate, endDate, limit: 1000, format: 'csv' };
    try {
      const r = await axios.get(`${API}/admin/finance/etats-financiers/drilldown/${type}`, {
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${startDate || 'now'}-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'CSV exporté' });
    } catch { toast({ title: 'Erreur export CSV', variant: 'destructive' }); }
  };

  const fmt = (v) => v == null ? '—' : `${Math.round(v).toLocaleString('fr-FR')}`;
  const titles = {
    'top-clients': 'Détail Top Clients',
    'outstanding': 'Détail Encours (Aging)',
    'operations': 'Détail Opérations',
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="drilldown-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="font-bold text-lg text-zinc-900">{titles[type]}</div>
            <div className="text-xs text-zinc-500">{items.length} ligne(s)</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="drill-export-csv">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> CSV
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="drill-close-btn">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {type === 'outstanding' && (
          <div className="p-4 border-b flex flex-wrap gap-1.5">
            {[
              { id: 'all', label: 'Toutes' },
              { id: '0_30', label: '0-30j', color: 'emerald' },
              { id: '31_60', label: '31-60j', color: 'amber' },
              { id: '61_90', label: '61-90j', color: 'orange' },
              { id: '91_plus', label: '> 90j', color: 'rose' },
            ].map(b => (
              <button
                key={b.id}
                onClick={() => setSelectedBucket(b.id)}
                data-testid={`bucket-${b.id}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                  selectedBucket === b.id
                    ? 'bg-[#16A34A] text-white border-[#16A34A]'
                    : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300'
                }`}
              >{b.label}</button>
            ))}
          </div>
        )}

        <div className="p-4">
          {loading && <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Chargement...</div>}
          {!loading && items.length === 0 && <div className="text-zinc-400 text-sm p-4 text-center">Aucune donnée</div>}
          {!loading && items.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-zinc-500 uppercase tracking-wide">
                  {type === 'top-clients' && <>
                    <th className="py-2">Client</th><th className="text-right">CA</th><th className="text-right">Marge%</th><th className="text-right">Ops</th><th className="text-right">Encours</th>
                  </>}
                  {type === 'outstanding' && <>
                    <th className="py-2">Facture</th><th>Client</th><th className="text-right">Montant</th><th className="text-right">Retard</th><th>Tranche</th>
                  </>}
                  {type === 'operations' && <>
                    <th className="py-2">N° op</th><th>Client</th><th>Trajet</th><th>Statut</th><th className="text-right">Marge</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 200).map((it, i) => (
                  <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                    {type === 'top-clients' && <>
                      <td className="py-2 font-medium">{it.name}</td>
                      <td className="text-right tabular-nums">{fmt(it.revenue)}</td>
                      <td className="text-right tabular-nums">{it.marginPct}%</td>
                      <td className="text-right">{it.ops}</td>
                      <td className={`text-right tabular-nums ${it.outstanding > 0 ? 'text-rose-600' : 'text-zinc-400'}`}>{fmt(it.outstanding)}</td>
                    </>}
                    {type === 'outstanding' && <>
                      <td className="py-2 font-medium">{it.invoiceNumber || '—'}</td>
                      <td>{it.client}</td>
                      <td className="text-right tabular-nums text-rose-600">{fmt(it.amount)}</td>
                      <td className="text-right tabular-nums">{it.daysOverdue}j</td>
                      <td><span className={`px-2 py-0.5 rounded text-[10px] ${
                        it.bucket === '0_30' ? 'bg-emerald-100 text-emerald-700' :
                        it.bucket === '31_60' ? 'bg-amber-100 text-amber-700' :
                        it.bucket === '61_90' ? 'bg-orange-100 text-orange-700' :
                        'bg-rose-100 text-rose-700'
                      }`}>{it.bucket.replace('_', '-').replace('plus', '+')}</span></td>
                    </>}
                    {type === 'operations' && <>
                      <td className="py-2 font-medium">{it.operationNumber}</td>
                      <td>{it.client}</td>
                      <td className="text-zinc-500">{it.departure} → {it.arrival}</td>
                      <td>{it.status}</td>
                      <td className="text-right tabular-nums">{fmt(it.margin)}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {items.length > 200 && <div className="text-center text-xs text-zinc-400 mt-3">+ {items.length - 200} autres lignes — utilisez l'export CSV</div>}
        </div>
      </div>
    </div>
  );
};


/* ================== EMAIL CONFIG DIALOG ================== */
const EmailConfigDialog = ({ open, onClose }) => {
  const [cfg, setCfg] = useState({ enabled: false, recipients: [], day_of_week: 0, hour_utc: 6 });
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/finance/cockpit/email-config`, {});
        setCfg(r.data);
      } catch {}
    })();
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/finance/cockpit/email-config`, cfg, {});
      toast({ title: 'Configuration enregistrée' });
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await axios.put(`${API}/admin/finance/cockpit/email-config`, cfg, {});
      const r = await axios.post(`${API}/admin/finance/cockpit/send-now`, {}, {});
      toast({ title: `${r.data.sent}/${r.data.results.length} envois OK` });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSending(false); }
  };

  const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="email-config-dialog">
        <DialogHeader><DialogTitle>📧 Rapport hebdomadaire par email</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="enabled-switch" className="text-sm">Activer l'envoi automatique</Label>
            <Switch id="enabled-switch" checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="email-enabled-switch" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Destinataires</Label>
            <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
              {(cfg.recipients || []).map((email) => (
                <Badge key={email} variant="secondary" className="text-xs gap-1.5">
                  {email}
                  <button onClick={() => setCfg({ ...cfg, recipients: cfg.recipients.filter((e) => e !== email) })}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input type="email" placeholder="cfo@exemple.ci" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && newEmail.includes('@')) { setCfg({ ...cfg, recipients: [...new Set([...(cfg.recipients || []), newEmail])] }); setNewEmail(''); } }}
                className="text-sm" data-testid="email-input" />
              <Button variant="outline" size="sm" onClick={() => { if (newEmail.includes('@')) { setCfg({ ...cfg, recipients: [...new Set([...(cfg.recipients || []), newEmail])] }); setNewEmail(''); } }} data-testid="email-add-btn">Ajouter</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Jour</Label>
              <select value={cfg.day_of_week} onChange={(e) => setCfg({ ...cfg, day_of_week: Number(e.target.value) })}
                className="w-full mt-1 h-9 rounded-md border border-zinc-200 text-sm px-2" data-testid="email-day-select">
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Heure (UTC)</Label>
              <Input type="number" min="0" max="23" value={cfg.hour_utc} onChange={(e) => setCfg({ ...cfg, hour_utc: Number(e.target.value) })}
                className="mt-1 text-sm tabular-nums" data-testid="email-hour-input" />
            </div>
          </div>
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={sendTest} disabled={sending || !cfg.recipients?.length} data-testid="send-now-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              Envoyer maintenant (test)
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="email-save-btn">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


/* ================== RETENTION TAB — Premium UX (PM Spec 10/05/2026) ================== */
const RETENTION_STATUS = {
  healthy:  { label: 'Healthy',     iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', badgeCls: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500' },
  at_risk:  { label: 'À surveiller',iconBg: 'bg-amber-100',   iconColor: 'text-amber-600',   badgeCls: 'bg-amber-50 text-amber-700 border-amber-200',       bar: 'bg-amber-500' },
  critical: { label: 'Critique',    iconBg: 'bg-rose-100',    iconColor: 'text-rose-600',    badgeCls: 'bg-rose-50 text-rose-700 border-rose-300',          bar: 'bg-rose-500' },
  lost:     { label: 'Perdu',       iconBg: 'bg-zinc-200',    iconColor: 'text-zinc-500',    badgeCls: 'bg-zinc-100 text-zinc-600 border-zinc-300',         bar: 'bg-zinc-400' },
};

const RetentionKpiCard = ({ icon: Icon, label, value, mom, iconColor, iconBg, isPositive, testId }) => {
  let display = '= 0%'; let cls = 'text-zinc-500';
  if (mom != null && mom !== 0) {
    display = `${mom > 0 ? '↑' : '↓'} ${Math.abs(mom)}%`;
    const isGood = (mom > 0 && isPositive) || (mom < 0 && !isPositive);
    cls = isGood ? 'text-emerald-600' : 'text-rose-600';
  }
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 transition-all hover:border-zinc-300" data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`h-9 w-9 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}><Icon className={`h-4 w-4 ${iconColor}`} /></div>
        <span className="text-xs font-medium text-zinc-600 leading-tight">{label}</span>
      </div>
      <div className="text-3xl font-bold tracking-tight text-zinc-950 tabular-nums">{value}</div>
      <div className="text-xs mt-1.5">
        <span className={`font-semibold ${cls}`}>{display}</span> <span className="text-zinc-500">vs mois précédent</span>
      </div>
    </div>
  );
};

const ScoreBreakdown = ({ b }) => (
  <div className="flex items-center gap-3 text-[10px] mt-1.5">
    {[
      { k: 'R', v: b.recency, m: 40 },
      { k: 'F', v: b.frequency, m: 25 },
      { k: 'T', v: b.trend, m: 25 },
      { k: 'I', v: b.incidents, m: 10 },
    ].map((it) => (
      <span key={it.k} className="text-zinc-500">
        <span className="font-bold text-zinc-700">{it.k}</span>
        <span className={`mx-0.5 font-bold tabular-nums ${it.v === 0 ? 'text-rose-600' : it.v < it.m / 2 ? 'text-amber-600' : 'text-emerald-600'}`}>{it.v}</span>
        <span className="text-zinc-400">/{it.m}</span>
      </span>
    ))}
  </div>
);

const CreateTaskDialog = ({ open, onClose, client, onCreated }) => {
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 48 * 3600000).toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (!open) { setNotes(''); setDueDate(new Date(Date.now() + 48 * 3600000).toISOString().slice(0, 10)); } }, [open]);
  if (!client) return null;
  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/finance/cockpit/retention-task`, {
        clientName: client.name, score: client.score, status: client.status,
        severity: client.recommendedAction?.severity, notes,
        actionLabel: client.recommendedAction?.label, dueDate,
      }, {});
      toast({ title: 'Tâche créée', description: `Relance commerciale assignée pour ${client.name}` });
      onCreated?.(r.data.task); onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec création', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="create-task-dialog">
        <DialogHeader><DialogTitle className="text-base">Créer une tâche de relance — {client.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
            <p className="font-medium text-amber-900">{client.recommendedAction?.label}</p>
            <p className="text-amber-700">{client.recommendedAction?.subtitle}</p>
            <p className="text-zinc-600 mt-1.5">Dernière commande : il y a {client.daysSinceLastOrder}j · Fréquence habituelle : {client.avgFrequencyDays}j</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Échéance</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 text-sm" data-testid="task-due-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Notes / contexte</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder={`Ex : Appeler ${client.name} avant ${dueDate}. Comprendre pourquoi commandes ralenties.`}
              className="mt-1 w-full text-sm rounded-md border border-zinc-200 px-3 py-2" data-testid="task-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="task-submit-btn">
              {submitting ? 'Création...' : 'Créer la tâche'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RetentionTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [taskClient, setTaskClient] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/finance/cockpit/retention-score?limit=200`, {});
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  if (loading) return <div className="text-center py-12 text-zinc-500 text-sm"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Calcul du score de rétention...</div>;
  if (!data?.allClients?.length) return <div className="text-center py-12 text-zinc-500 text-sm">Aucun client actif sur les 6 derniers mois.</div>;

  const filtered = data.allClients.filter((c) => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'all') return true;
    if (filter === 'churn') return c.isChurnRisk;
    return c.status === filter;
  });

  const s = data.summary;
  const priorities = data.priorityClients || [];

  return (
    <div className="space-y-4" data-testid="tab-retention">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-zinc-950">Surveillance rétention clients</h2>
        <p className="text-sm text-zinc-500 mt-1">Identifiez les clients à risque avant qu'ils ne cessent de commander.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="retention-kpis">
        <RetentionKpiCard testId="retk-healthy"  icon={CheckCircle2}   label="Clients sains"       value={s.healthy.count}  mom={s.healthy.mom}  iconColor="text-emerald-600" iconBg="bg-emerald-100" isPositive />
        <RetentionKpiCard testId="retk-at-risk"  icon={AlertCircle}    label="À surveiller"        value={s.at_risk.count}  mom={s.at_risk.mom}  iconColor="text-amber-600"   iconBg="bg-amber-100"   isPositive={false} />
        <RetentionKpiCard testId="retk-critical" icon={AlertTriangle}  label="Critiques"           value={s.critical.count} mom={s.critical.mom} iconColor="text-rose-600"    iconBg="bg-rose-100"    isPositive={false} />
        <RetentionKpiCard testId="retk-lost"     icon={Users}          label="Perdus"              value={s.lost.count}     mom={s.lost.mom}     iconColor="text-zinc-500"    iconBg="bg-zinc-200"    isPositive={false} />
        <RetentionKpiCard testId="retk-churn"    icon={Zap}            label="Signal churn actif"  value={s.churn.count}    mom={s.churn.mom}    iconColor="text-rose-600"    iconBg="bg-rose-100"    isPositive={false} />
      </div>

      {priorities.length > 0 && (
        <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="priority-banner">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0"><Zap className="h-4 w-4 text-emerald-700" /></div>
            <p className="text-sm text-zinc-800">
              <strong className="text-zinc-950">{s.churn.count} client{s.churn.count > 1 ? 's' : ''} présent{s.churn.count > 1 ? 'ent' : 'e'} un signal de churn.</strong> Priorité : relancer <strong className="text-emerald-700">{priorities.join(' et ')}</strong> cette semaine.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setFilter('churn')} data-testid="priority-detail-btn">Voir le détail</Button>
        </div>
      )}

      <div className="flex items-start gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3" data-testid="explainer-card">
        <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0"><AlertCircle className="h-4 w-4 text-blue-600" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-950">Comment est calculé le score ?</p>
          <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
            Le score combine 4 critères : <strong>dernière commande</strong>, <strong>fréquence habituelle</strong>, <strong>évolution du chiffre d'affaires</strong> et <strong>incidents</strong>. Un <strong className="text-rose-600">signal churn</strong> apparaît lorsqu'un client dépasse 2 fois sa fréquence habituelle sans nouvelle commande.
          </p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl px-3 py-2.5 flex items-center gap-2 flex-wrap" data-testid="filter-bar">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Rechercher un client..."
          className="h-9 pl-3 pr-3 rounded-md border border-zinc-200 text-sm w-56" data-testid="retention-search" />
        {[
          { k: 'all', label: 'Tous', cls: filter === 'all' ? 'bg-[#16A34A] text-white border-[#16A34A]' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50' },
          { k: 'healthy', label: 'Healthy', cls: filter === 'healthy' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-emerald-50/60 text-emerald-700 border-emerald-200' },
          { k: 'at_risk', label: 'À surveiller', cls: filter === 'at_risk' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-amber-50/60 text-amber-700 border-amber-200' },
          { k: 'churn', label: 'Churn signal', cls: filter === 'churn' ? 'bg-rose-100 text-rose-700 border-rose-300' : 'bg-rose-50/60 text-rose-700 border-rose-200' },
          { k: 'critical', label: 'Critique', cls: filter === 'critical' ? 'bg-rose-200 text-rose-800 border-rose-400' : 'bg-rose-50/60 text-rose-700 border-rose-200' },
          { k: 'lost', label: 'Perdu', cls: filter === 'lost' ? 'bg-zinc-200 text-zinc-800 border-zinc-400' : 'bg-zinc-100 text-zinc-600 border-zinc-200' },
        ].map((p) => (
          <button key={p.k} onClick={() => setFilter(p.k)} className={`h-7 px-3 rounded-full border text-xs font-medium transition ${p.cls}`} data-testid={`filter-pill-${p.k}`}>{p.label}</button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="retention-table">
            <thead className="bg-zinc-50/60 border-b border-zinc-200">
              <tr className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                <th className="text-left px-3 py-2.5 w-10">#</th>
                <th className="text-left px-3 py-2.5">Client</th>
                <th className="text-left px-3 py-2.5 min-w-[200px]">Score</th>
                <th className="text-left px-3 py-2.5">Dernière commande &amp; fréquence</th>
                <th className="text-left px-3 py-2.5">Tendance 3 mois</th>
                <th className="text-center px-3 py-2.5">Incidents 6 mois</th>
                <th className="text-left px-3 py-2.5">Action recommandée</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-zinc-400 italic">Aucun client ne correspond aux filtres.</td></tr>
              ) : filtered.map((c, i) => {
                const m = RETENTION_STATUS[c.status] || RETENTION_STATUS.at_risk;
                const action = c.recommendedAction || {};
                const initials = c.name.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
                const rowBg = c.isChurnRisk ? 'bg-rose-50/30 hover:bg-rose-50/50' : 'hover:bg-zinc-50/60';
                return (
                  <tr key={c.name} className={`border-b border-zinc-100 last:border-0 transition ${rowBg}`} data-testid={`retention-row-${i}`}>
                    <td className="px-3 py-3 align-top font-mono text-xs text-zinc-400">{i + 1}</td>
                    <td className="px-3 py-3 align-top min-w-[200px]">
                      <div className="flex items-center gap-2.5">
                        <div className={`h-9 w-9 rounded-md ${m.iconBg} flex items-center justify-center shrink-0 text-[10px] font-bold ${m.iconColor}`}>{initials}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-zinc-950 truncate">{c.name}</span>
                            {c.isChurnRisk ? <Badge className="bg-rose-100 text-rose-700 border-rose-300 text-[10px] px-1.5 py-0 h-4">Churn signal</Badge>
                                          : <Badge variant="outline" className={`${m.badgeCls} text-[10px] px-1.5 py-0 h-4`}>{m.label}</Badge>}
                          </div>
                          <p className="text-xs text-zinc-500 tabular-nums">{fxof(c.revenue6m)} (6 mois)</p>
                          <p className="text-xs text-zinc-500">{c.ordersCount6m} commande{c.ordersCount6m > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex items-baseline gap-1.5">
                        <span className={`text-2xl font-bold tabular-nums ${c.score >= 75 ? 'text-emerald-700' : c.score >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>{c.score}</span>
                        <span className="text-xs text-zinc-400">/100</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100 rounded-full mt-1.5 w-40 overflow-hidden">
                        <div className={`h-full ${m.bar} transition-all`} style={{ width: `${c.score}%` }} />
                      </div>
                      <ScoreBreakdown b={c.breakdown} />
                    </td>
                    <td className="px-3 py-3 align-top">
                      <p className="text-xs text-zinc-500">Dernière commande :</p>
                      <p className={`text-sm font-semibold ${c.daysSinceLastOrder > c.churnThresholdDays ? 'text-rose-700' : c.daysSinceLastOrder > c.avgFrequencyDays ? 'text-amber-700' : 'text-zinc-900'}`}>
                        Il y a {c.daysSinceLastOrder} jour{c.daysSinceLastOrder > 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-zinc-500 tabular-nums mt-1">Fréquence habituelle : {c.avgFrequencyDays} j</p>
                    </td>
                    <td className="px-3 py-3 align-top">
                      {c.trendPct == null ? <span className="text-xs text-zinc-400 italic">Insuffisant</span> : (
                        <>
                          <p className={`text-sm font-bold tabular-nums flex items-center gap-1 ${c.trendPct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {c.trendPct >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                            {c.trendPct >= 0 ? '+' : ''}{c.trendPct}%
                          </p>
                          <p className="text-xs text-zinc-500 tabular-nums mt-1">{fmt(c.revenueRecent3m)} vs {fmt(c.revenuePrev3m)}</p>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top text-center">
                      <div className={`text-xl font-bold tabular-nums ${c.incidentsCount > 2 ? 'text-rose-700' : c.incidentsCount > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{c.incidentsCount}</div>
                      <div className="text-[10px] text-zinc-500">incidents</div>
                    </td>
                    <td className="px-3 py-3 align-top min-w-[200px]">
                      <p className={`text-xs font-semibold leading-tight ${action.severity === 'high' ? 'text-rose-700' : action.severity === 'medium' ? 'text-amber-700' : action.severity === 'ok' ? 'text-emerald-700' : 'text-zinc-700'}`}>{action.label}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{action.subtitle}</p>
                      {action.cta && (
                        <Button size="sm" onClick={() => setTaskClient(c)}
                          className={`mt-2 h-7 text-xs ${action.severity === 'high' ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                          data-testid={`create-task-btn-${i}`}>{action.cta}</Button>
                      )}
                    </td>
                    <td className="px-2 py-3 align-top text-center">
                      <button className="h-7 w-7 rounded hover:bg-zinc-100 text-zinc-400" title="Plus d'options">⋮</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CreateTaskDialog open={!!taskClient} onClose={() => setTaskClient(null)} client={taskClient} onCreated={fetchData} />
    </div>
  );
};

/* ================== MAIN PAGE ================== */
const TREND_PRESETS = [
  { v: '30d', l: '30 j' },
  { v: '90d', l: '90 j' },
  { v: '180d', l: '6 mois' },
  { v: 'this_month', l: 'Ce mois' },
  { v: 'this_year', l: 'Cette année' },
  { v: '12m', l: '12 mois' },
];

const PaidVsUnpaidChart = () => {
  const [preset, setPreset] = useState('12m');
  const [manual, setManual] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [basis, setBasis] = useState('invoice');
  const [view, setView] = useState('line'); // line | area | bar
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTrend = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ basis });
      if (manual && from && to) {
        params.set('date_from', from);
        params.set('date_to', to);
      } else {
        params.set('preset', preset);
      }
      const r = await axios.get(`${API}/admin/finance/cockpit/trend?${params.toString()}`, {});
      setData(r.data);
    } catch (err) {
      toast({ title: 'Erreur courbe payé/impayé', description: err?.response?.data?.detail || 'Erreur réseau', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [preset, manual, from, to, basis]);

  useEffect(() => {
    if (manual && (!from || !to)) return;
    fetchTrend();
  }, [fetchTrend, manual, from, to]);

  const COLORS = { paid: '#16A34A', unpaid: '#f43f5e' };
  const t = data?.totals;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="paid-unpaid-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-950 flex items-center gap-1.5">
            <Coins className="h-4 w-4 text-[#16A34A]" /> Impayés vs Payés
          </h3>
          <p className="text-xs text-zinc-500">
            {basis === 'cash' ? 'Encaissements réels vs créances émises' : 'Réglé vs reste dû par date de facture'}
            {data ? ` · ${data.granularity === 'day' ? 'par jour' : data.granularity === 'week' ? 'par semaine' : 'par mois'}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Base */}
          <div className="flex rounded-md border border-zinc-200 overflow-hidden" data-testid="trend-basis-toggle">
            {[{ v: 'invoice', l: 'Facture' }, { v: 'cash', l: 'Encaissement' }].map((b) => (
              <button key={b.v} onClick={() => setBasis(b.v)}
                className={`px-2.5 py-1 text-xs transition-colors ${basis === b.v ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                data-testid={`trend-basis-${b.v}`}>{b.l}</button>
            ))}
          </div>
          {/* Type de visuel */}
          <div className="flex rounded-md border border-zinc-200 overflow-hidden" data-testid="trend-view-toggle">
            {[{ v: 'line', l: 'Lignes' }, { v: 'area', l: 'Aires' }, { v: 'bar', l: 'Barres' }].map((b) => (
              <button key={b.v} onClick={() => setView(b.v)}
                className={`px-2.5 py-1 text-xs transition-colors ${view === b.v ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                data-testid={`trend-view-${b.v}`}>{b.l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Filtres période */}
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {TREND_PRESETS.map((p) => (
          <button key={p.v} onClick={() => { setManual(false); setPreset(p.v); }}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${!manual && preset === p.v ? 'bg-[#16A34A] text-white border-[#16A34A]' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
            data-testid={`trend-preset-${p.v}`}>{p.l}</button>
        ))}
        <div className="flex items-center gap-1.5 ml-1">
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setManual(true); }}
            className="h-7 text-xs w-[140px]" data-testid="trend-date-from" />
          <span className="text-xs text-zinc-400">→</span>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setManual(true); }}
            className="h-7 text-xs w-[140px]" data-testid="trend-date-to" />
        </div>
      </div>

      {/* Mini-totaux */}
      {t && (
        <div className="flex items-center gap-4 mb-3 text-xs" data-testid="trend-totals">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS.paid }} /><span className="text-zinc-500">Payé</span> <span className="font-semibold tabular-nums text-zinc-900">{fxof(t.paid)}</span> <span className="text-emerald-600">({t.paidPct}%)</span></span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS.unpaid }} /><span className="text-zinc-500">Impayé</span> <span className="font-semibold tabular-nums text-zinc-900">{fxof(t.unpaid)}</span> <span className="text-rose-600">({t.unpaidPct}%)</span></span>
        </div>
      )}

      {loading ? (
        <div className="h-[260px] bg-zinc-50 rounded-lg animate-pulse" data-testid="trend-loading" />
      ) : !data || data.series.every((s) => s.total === 0) ? (
        <div className="h-[260px] flex items-center justify-center text-sm text-zinc-400 italic" data-testid="trend-empty">
          Aucune facture sur cette période
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260} minWidth={0}>
          {view === 'bar' ? (
            <BarChart data={data.series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveStartEnd" />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#71717a' }} width={50} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }} formatter={(v, n) => [fxof(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="paid" stackId="s" fill={COLORS.paid} name="Payé" radius={[0, 0, 0, 0]} />
              <Bar dataKey="unpaid" stackId="s" fill={COLORS.unpaid} name="Impayé" radius={[3, 3, 0, 0]} />
            </BarChart>
          ) : view === 'area' ? (
            <AreaChart data={data.series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.paid} stopOpacity={0.4} /><stop offset="100%" stopColor={COLORS.paid} stopOpacity={0.04} /></linearGradient>
                <linearGradient id="gUnpaid" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.unpaid} stopOpacity={0.4} /><stop offset="100%" stopColor={COLORS.unpaid} stopOpacity={0.04} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveStartEnd" />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#71717a' }} width={50} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }} formatter={(v, n) => [fxof(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="paid" stackId="s" stroke={COLORS.paid} fill="url(#gPaid)" name="Payé" />
              <Area type="monotone" dataKey="unpaid" stackId="s" stroke={COLORS.unpaid} fill="url(#gUnpaid)" name="Impayé" />
            </AreaChart>
          ) : (
            <LineChart data={data.series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveStartEnd" />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#71717a' }} width={50} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }} formatter={(v, n) => [fxof(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="paid" stroke={COLORS.paid} strokeWidth={2} dot={false} name="Payé" />
              <Line type="monotone" dataKey="unpaid" stroke={COLORS.unpaid} strokeWidth={2} dot={false} name="Impayé" />
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
};

const FneCoverageChart = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/fne-regularization/coverage?months=12`, {});
        setData(r.data);
      } catch (err) {
        toast({ title: 'Erreur couverture FNE', description: err?.response?.data?.detail || 'Erreur réseau', variant: 'destructive' });
      } finally { setLoading(false); }
    })();
  }, []);

  const t = data?.totals;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="fne-coverage-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-950 flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4 text-blue-600" /> FNE générées vs factures Baobab
          </h3>
          <p className="text-xs text-zinc-500">Écart mensuel · 1 facture Baobab (client FNE) = 1 facture FNE attendue</p>
        </div>
        {t && (
          <div className="flex items-center gap-4 text-xs" data-testid="fne-coverage-totals">
            <span><span className="text-zinc-500">Baobab</span> <span className="font-semibold tabular-nums">{t.baobab}</span></span>
            <span><span className="text-zinc-500">FNE</span> <span className="font-semibold tabular-nums text-emerald-700">{t.fne}</span></span>
            <span><span className="text-zinc-500">Écart</span> <span className="font-semibold tabular-nums text-rose-600">{t.gap}</span></span>
            <Badge variant="outline" className={`text-[10px] ${t.coveragePct >= 90 ? 'border-emerald-300 text-emerald-700' : 'border-rose-300 text-rose-700'}`}>
              {t.coveragePct}% couvert
            </Badge>
          </div>
        )}
      </div>
      {loading ? (
        <div className="h-[240px] bg-zinc-50 rounded-lg animate-pulse" />
      ) : !data ? null : (
        <ResponsiveContainer width="100%" height={240} minWidth={0}>
          <LineChart data={data.series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#71717a' }} width={36} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="baobab" stroke="#3b82f6" strokeWidth={2} dot={false} name="Factures Baobab" />
            <Line type="monotone" dataKey="fne" stroke="#16A34A" strokeWidth={2} dot={false} name="FNE générées" />
            <Line type="monotone" dataKey="gap" stroke="#f43f5e" strokeWidth={2} strokeDasharray="5 4" dot={false} name="Écart (à régulariser)" />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const REG_STATUS_META = {
  sla_breached: { label: 'Hors SLA', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertOctagon },
  blocked: { label: 'Info manquante', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: FileX },
  ready: { label: 'À certifier', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
};

const FneRegularizationTab = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerting, setAlerting] = useState(null); // commercialId | 'all'

  const fetchScan = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/fne-regularization/scan`, {});
      setData(r.data);
    } catch (err) {
      toast({ title: 'Erreur scan FNE', description: err?.response?.data?.detail || 'Erreur réseau', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchScan(); }, [fetchScan]);

  const sendAlert = async (commercialId) => {
    setAlerting(commercialId || 'all');
    try {
      const r = await axios.post(`${API}/admin/fne-regularization/alert`, commercialId ? { commercialId } : {}, {});
      const d = r.data;
      toast({ title: 'Commercial alerté', description: `${d.totalInvoicesAlerted} facture(s) signalée(s) à ${d.alertedCommercials} commercial(aux)${d.results?.some((x) => x.emailSent) ? ' (email + in-app)' : ' (in-app)'}` });
      fetchScan();
    } catch (err) {
      toast({ title: 'Erreur alerte', description: err?.response?.data?.detail || 'Erreur réseau', variant: 'destructive' });
    } finally { setAlerting(null); }
  };

  if (loading) return <div className="h-64 bg-zinc-100 rounded-xl animate-pulse" data-testid="fne-reg-loading" />;
  if (!data) return null;
  const s = data.summary;

  return (
    <div className="space-y-4" data-testid="fne-regularization-tab">
      {/* Header + actions */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-zinc-950">Régularisation FNE</h3>
          <p className="text-xs text-zinc-500">Factures Baobab de clients FNE non encore certifiées · SLA {data.slaHours}h</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchScan} data-testid="fne-reg-rescan-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Scanner maintenant
          </Button>
          <Button size="sm" onClick={() => sendAlert(null)} disabled={alerting === 'all' || s.pending === 0}
            className="bg-rose-600 hover:bg-rose-700" data-testid="fne-reg-alert-all-btn">
            {alerting === 'all' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BellRing className="h-4 w-4 mr-1.5" />}
            Alerter tous les commerciaux
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fne-reg-kpis">
        {[
          { k: 'pending', label: 'À régulariser', v: s.pending, color: 'text-zinc-900', icon: FileText },
          { k: 'slaBreached', label: `Hors SLA (${data.slaHours}h)`, v: s.slaBreached, color: 'text-rose-600', icon: AlertOctagon },
          { k: 'blocked', label: 'Info manquante', v: s.blocked, color: 'text-amber-600', icon: FileX },
          { k: 'amount', label: 'Montant concerné', v: fxof(s.totalAmount), color: 'text-zinc-900', icon: Coins },
        ].map((kpi) => (
          <div key={kpi.k} className="bg-white border border-zinc-200 rounded-xl p-3" data-testid={`fne-reg-kpi-${kpi.k}`}>
            <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1"><kpi.icon className="h-3.5 w-3.5" /> {kpi.label}</div>
            <div className={`text-xl font-bold tabular-nums ${kpi.color}`}>{kpi.v}</div>
          </div>
        ))}
      </div>

      {s.pending === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="fne-reg-empty">
          <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-emerald-800">Toutes les factures FNE sont à jour 🎉</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.byCommercial.map((grp) => (
            <div key={grp.commercialId} className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={`fne-reg-commercial-${grp.commercialId}`}>
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">
                    {(grp.commercialName || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">{grp.commercialName}</p>
                    <p className="text-xs text-zinc-500">
                      {grp.pending} facture(s) · {grp.slaBreached} hors SLA · {grp.blocked} bloquée(s) · {fxof(grp.totalAmount)}
                    </p>
                  </div>
                </div>
                <Button size="sm" onClick={() => sendAlert(grp.commercialId)} disabled={alerting === grp.commercialId}
                  className="bg-rose-600 hover:bg-rose-700" data-testid={`fne-reg-alert-btn-${grp.commercialId}`}>
                  {alerting === grp.commercialId ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                  Alerter
                </Button>
              </div>
              <div className="divide-y divide-zinc-100">
                {grp.invoices.map((inv) => {
                  const meta = REG_STATUS_META[inv.status] || REG_STATUS_META.ready;
                  return (
                    <div key={inv.recordId} className="flex items-center justify-between gap-3 py-2" data-testid={`fne-reg-invoice-${inv.recordId}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <button onClick={() => navigate(`/admin/operations/${inv.recordId}`)}
                            className="text-sm font-medium text-blue-700 hover:underline truncate">{inv.invoiceNumber}</button>
                          <span className="text-xs text-zinc-500 truncate">· {inv.companyName}</span>
                        </div>
                        {inv.missingFields.length > 0 && (
                          <p className="text-xs text-amber-600 mt-0.5">Manque : {inv.missingFields.join(', ')}</p>
                        )}
                      </div>
                      <span className="text-xs tabular-nums text-zinc-500 shrink-0">{Math.round(inv.hoursSinceInvoice)}h</span>
                      <Badge variant="outline" className={`text-[10px] shrink-0 ${meta.cls}`}>
                        <meta.icon className="h-3 w-3 mr-1" /> {meta.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {data.unassigned && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="fne-reg-unassigned">
              <div className="flex items-center gap-2 mb-1">
                <ShieldAlert className="h-4 w-4 text-amber-600" />
                <p className="text-sm font-semibold text-amber-800">{data.unassigned.pending} facture(s) sans commercial assigné</p>
              </div>
              <p className="text-xs text-amber-700">
                Impossible d'alerter — assignez un commercial à ces clients pour activer le suivi FNE. Montant : {fxof(data.unassigned.totalAmount)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ================== TREASURY HERO CARD — Tour de contrôle Cash (Wave + Banque) ================== */
const TreasuryHeroCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSnapshot = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await axios.get(`${API}/wave/treasury/snapshot`, {});
      setData(r.data);
    } catch (e) {
      // silencieux : la carte affiche un fallback
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  if (loading) {
    return <div className="h-[132px] rounded-xl bg-gradient-to-br from-zinc-100 to-zinc-50 animate-pulse" data-testid="treasury-hero-loading" />;
  }
  if (!data) return null;

  const w = data.wave || {};
  const b = data.bank || {};
  const variation = w.variation7d;
  const variationPct = w.variation7dPct;
  const isPositive = variation != null && variation >= 0;
  const VarIcon = isPositive ? TrendingUp : TrendingDown;

  const lastSyncLabel = w.lastSyncAt
    ? new Date(w.lastSyncAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : 'Jamais synchronisé';

  return (
    <div
      className="relative rounded-xl border border-zinc-200 bg-gradient-to-br from-emerald-50 via-white to-blue-50 overflow-hidden"
      data-testid="treasury-hero-card"
    >
      {/* Decorative sparkle */}
      <div className="absolute top-4 right-4 opacity-20">
        <Sparkles className="h-14 w-14 text-emerald-600" />
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-[1.2fr,1fr,1fr] gap-5 relative">
        {/* Colonne 1 — Solde consolidé */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Tour de contrôle Cash · temps réel</span>
            <button
              onClick={() => fetchSnapshot(true)}
              disabled={refreshing}
              className="ml-auto lg:hidden text-zinc-400 hover:text-zinc-700"
              data-testid="treasury-refresh-btn-mobile"
              title="Rafraîchir"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="text-4xl font-bold tracking-tight text-zinc-950 tabular-nums" data-testid="treasury-total-balance">
            {data.total != null ? fxof(data.total) : '—'}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {variation != null && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${isPositive ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}
                data-testid="treasury-variation-7d"
              >
                <VarIcon className="h-3 w-3" />
                {isPositive ? '+' : ''}{fmt(variation)} XOF
                {variationPct != null && <span className="opacity-75">({isPositive ? '+' : ''}{variationPct.toFixed(1)}%)</span>}
                <span className="font-medium opacity-75">/ 7j</span>
              </span>
            )}
            <span className="text-xs text-zinc-500 inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> Synchronisé le {lastSyncLabel}
            </span>
          </div>
        </div>

        {/* Colonne 2 — Wave */}
        <div className="border-l border-zinc-200 lg:pl-5" data-testid="treasury-source-wave">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-7 w-7 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Wallet className="h-3.5 w-3.5 text-emerald-700" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700">Wave</span>
            {w.available && (
              <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide bg-emerald-600 text-white px-1.5 py-0.5 rounded">
                Live
              </span>
            )}
          </div>
          <div className="text-2xl font-bold tabular-nums text-emerald-800" data-testid="treasury-wave-balance">
            {w.balance != null ? fxof(w.balance) : '—'}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {w.txCount7d != null ? `${w.txCount7d} transaction${w.txCount7d > 1 ? 's' : ''} sur 7j` : 'Aucune donnée'}
          </div>
          {/* Mini-sparkline 30j — évolution du solde Wave */}
          {Array.isArray(w.sparkline30d) && w.sparkline30d.length > 1 && (
            <div className="mt-2 h-10 -mx-1" data-testid="treasury-wave-sparkline">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={w.sparkline30d} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sparkWaveFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#16A34A" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    cursor={{ stroke: '#16A34A', strokeWidth: 1, strokeDasharray: '3 3' }}
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e4e4e7', padding: '4px 8px' }}
                    labelFormatter={(d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    formatter={(v) => [fxof(v), 'Solde']}
                  />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke="#16A34A"
                    strokeWidth={1.5}
                    fill="url(#sparkWaveFill)"
                    isAnimationActive={false}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="text-[9px] text-zinc-400 mt-0.5 flex items-center justify-between">
                <span>30 j</span>
                <span>Aujourd&apos;hui</span>
              </div>
            </div>
          )}
        </div>

        {/* Colonne 3 — Banque (multi-comptes ou soon) */}
        <div className="border-l border-zinc-200 lg:pl-5 relative" data-testid="treasury-source-bank">
          <div className="flex items-center gap-2 mb-1">
            <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${b.status === 'active' ? 'bg-blue-100' : 'bg-zinc-100'}`}>
              <Landmark className={`h-3.5 w-3.5 ${b.status === 'active' ? 'text-blue-700' : 'text-zinc-500'}`} />
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-[0.12em] ${b.status === 'active' ? 'text-blue-700' : 'text-zinc-500'}`}>Banque</span>
            {b.status === 'active' ? (
              <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide bg-blue-600 text-white px-1.5 py-0.5 rounded">
                {b.accountsCount} compte{b.accountsCount > 1 ? 's' : ''}
              </span>
            ) : (
              <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded">
                Bientôt
              </span>
            )}
          </div>
          {b.status === 'active' && b.balance != null ? (
            <>
              <div className="text-2xl font-bold tabular-nums text-blue-800" data-testid="treasury-bank-balance">
                {fxof(b.balance)}
              </div>
              <div className="text-xs text-zinc-500 mt-1 space-y-0.5">
                {(b.accounts || []).slice(0, 3).map((acc) => (
                  <div key={acc.id} className="flex items-center gap-1.5 truncate" title={`${acc.bank_name} — ${acc.account_number}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="truncate">{acc.bank_name}</span>
                    <span className="tabular-nums text-zinc-700 ml-auto">{fmt(acc.balance)}</span>
                  </div>
                ))}
                {(b.accounts || []).length > 3 && (
                  <div className="text-[10px] text-zinc-400">+ {b.accounts.length - 3} autre(s)…</div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold tabular-nums text-zinc-300">
                —
              </div>
              <div className="text-xs text-zinc-400 mt-1 italic">
                <button
                  onClick={() => navigate('/admin/rapprochement/banque')}
                  className="text-blue-600 hover:underline"
                  data-testid="treasury-add-bank-link"
                >
                  Ajouter un compte bancaire →
                </button>
              </div>
            </>
          )}
        </div>

        {/* Refresh desktop */}
        <button
          onClick={() => fetchSnapshot(true)}
          disabled={refreshing}
          className="hidden lg:flex absolute top-3 right-3 items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 bg-white/70 backdrop-blur px-2 py-1 rounded-md border border-zinc-200"
          data-testid="treasury-refresh-btn"
          title="Rafraîchir le snapshot"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Sync…' : 'Rafraîchir'}
        </button>
      </div>
    </div>
  );
};


const FinanceCockpitPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [budget, setBudget] = useState(null);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [etatsOpen, setEtatsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('tab') || 'overview';
  });
  // Prévision de trésorerie : admin + finance uniquement (Lot B)
  const { user } = useAuth();
  const canSeeForecast = ['admin', 'super_admin', 'finance'].includes(user?.role);

  // Sync URL ↔ activeTab (l'onglet Wave a migré vers /admin/rapprochement/wave)
  useEffect(() => {
    if (activeTab === 'wave') {
      navigate('/admin/rapprochement/wave', { replace: true });
      return;
    }
    const sp = new URLSearchParams(window.location.search);
    if (activeTab === 'overview') sp.delete('tab'); else sp.set('tab', activeTab);
    const qs = sp.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : '');
    window.history.replaceState({}, '', newUrl);
  }, [activeTab, navigate]);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const headers = { Authorization: 'Bearer cookie-auth' };
      const [cockpit, bud] = await Promise.all([
        axios.get(`${API}/admin/finance/cockpit`, { headers }),
        axios.get(`${API}/admin/finance/cockpit/budget`, { headers }),
      ]);
      setData(cockpit.data);
      setBudget(bud.data);
    } catch (err) {
      toast({ title: 'Erreur cockpit', description: err?.response?.data?.detail || 'Erreur réseau', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveBudget = async (payload) => {
    try {
      await axios.put(`${API}/admin/finance/cockpit/budget`, payload, {});
      toast({ title: 'Budget mis à jour' });
      setBudgetOpen(false);
      fetchData(true);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Erreur réseau', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-1" data-testid="cockpit-loading">
        <div className="h-8 w-64 bg-zinc-100 rounded animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 bg-zinc-100 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-56 bg-zinc-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!data) return null;
  const k = data.kpis;

  return (
    <div className="space-y-4" data-testid="finance-cockpit">
      {/* ============== HEADER ============== */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-[#16A34A]" />
            Cockpit CFO
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Vue exécutive · 12 mois glissants · Mis à jour {new Date(data.asOf).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <AiInsightsButton />
          <Button variant="outline" size="sm" onClick={() => setEtatsOpen(true)} data-testid="etats-financiers-btn">
            <FileText className="h-4 w-4 mr-1.5" /> États Financiers
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const r = await axios.get(`${API}/admin/finance/cockpit/pdf`, { responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `cockpit-cfo-${new Date().toISOString().slice(0, 10)}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) { toast({ title: 'Erreur PDF', variant: 'destructive' }); }
          }} data-testid="pdf-download-btn">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const r = await axios.get(`${API}/admin/finance/cockpit/export-ohada`, { responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `journal-ventes-ohada-${new Date().toISOString().slice(0, 10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              toast({ title: 'Export OHADA téléchargé' });
            } catch (e) { toast({ title: 'Erreur export', variant: 'destructive' }); }
          }} data-testid="ohada-export-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> OHADA
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEmailOpen(true)} data-testid="email-config-btn">
            <Mail className="h-4 w-4 mr-1.5" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBudgetOpen(true)} data-testid="budget-config-btn">
            <Settings className="h-4 w-4 mr-1.5" /> Budget
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>
      </div>

      {/* ============== TABS ============== */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full flex-wrap justify-start gap-1 h-auto bg-zinc-100 p-1 md:w-fit" data-testid="cockpit-tabs">
          <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-white" data-testid="tab-overview">Vue exécutive</TabsTrigger>
          <TabsTrigger value="treasury" className="text-xs data-[state=active]:bg-white" data-testid="tab-treasury">Trésorerie globale</TabsTrigger>
          {canSeeForecast && (
            <TabsTrigger value="forecast" className="text-xs data-[state=active]:bg-white" data-testid="tab-forecast">Prévision 13 sem.</TabsTrigger>
          )}
          <TabsTrigger value="cashflow" className="text-xs data-[state=active]:bg-white" data-testid="tab-cashflow-trigger">Flux de trésorerie</TabsTrigger>
          <TabsTrigger value="fne-reg" className="text-xs data-[state=active]:bg-white" data-testid="tab-fne-reg">Régularisation FNE</TabsTrigger>
          <TabsTrigger value="profitability" className="text-xs data-[state=active]:bg-white" data-testid="tab-profit">Profitabilité clients</TabsTrigger>
          <TabsTrigger value="client-margin" className="text-xs data-[state=active]:bg-white" data-testid="tab-client-margin-trigger">Rentabilité par client</TabsTrigger>
          <TabsTrigger value="retention" className="text-xs data-[state=active]:bg-white" data-testid="tab-retention-trigger">Rétention clients</TabsTrigger>
          <TabsTrigger value="dpo" className="text-xs data-[state=active]:bg-white" data-testid="tab-dpo">DPO / CCC</TabsTrigger>
          <TabsTrigger value="stress" className="text-xs data-[state=active]:bg-white" data-testid="tab-stress">Stress test</TabsTrigger>
          <TabsTrigger value="hidden-tax" className="text-xs data-[state=active]:bg-white" data-testid="tab-hidden-tax">Taxe 2 %</TabsTrigger>
          <TabsTrigger value="audit" className="text-xs data-[state=active]:bg-white" data-testid="tab-audit">Audit factures</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4 space-y-4">
          {/* Seuil de régime d'imposition (CGI) — anticipation du changement de régime */}
          <RegimeThresholdBanner />

          {/* ============== TREASURY HERO — Tour de contrôle Cash (Wave + Banque) ============== */}
          <TreasuryHeroCard />

          {/* ============== ALERTS ============== */}
      {data.alerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="alerts-grid">
          {data.alerts.map((a, i) => <AlertCard key={i} alert={a} />)}
        </div>
      )}

      {/* ============== KPI HERO STRIP — Row 1: Revenue & Margin ============== */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-row-1">
        <KpiCard testId="kpi-revenue-12m" label="CA 12 mois (reconnu)" value={fxof(k.revenue12m)}
          sub={k.revenue12mInvoiced ? `dont ${fxof(k.revenue12mInvoiced)} facturé` : "Spot + contrat"}
          icon={DollarSign} accent="emerald" delta={k.revenueYoYPct} deltaLabel="YoY" />
        <KpiCard testId="kpi-revenue-month" label="CA mois en cours" value={fxof(k.revenueCurrentMonth)}
          sub={`vs ${fxof(k.revenuePrevMonth)} mois -1`} icon={Activity} accent="emerald" delta={k.revenueMoMPct} deltaLabel="MoM" />
        <KpiCard testId="kpi-gross-margin" label="Marge brute 12m" value={fxof(k.grossMargin12m)}
          sub="CA − coût transporteurs" icon={TrendingUp} accent="emerald" />
        <KpiCard testId="kpi-margin-pct" label="Marge brute %" value={`${k.grossMarginPct.toFixed(1)}%`}
          sub="Sur 12 mois glissants" icon={Target}
          accent={k.grossMarginPct >= 20 ? 'emerald' : k.grossMarginPct >= 15 ? 'amber' : 'rose'}
          alert={k.grossMarginPct < 15} />
        <KpiCard testId="kpi-budget" label="Budget atteint"
          value={k.budgetAttainmentPct != null ? `${k.budgetAttainmentPct}%` : '—'}
          sub={k.budgetTarget > 0 ? `Cible: ${fxof(k.budgetTarget)}/mois` : 'Cliquer "Budget" pour configurer'}
          icon={BarChart3} accent={k.budgetAttainmentPct == null ? 'zinc' : k.budgetAttainmentPct >= 100 ? 'emerald' : k.budgetAttainmentPct >= 90 ? 'amber' : 'rose'}
          onClick={() => setBudgetOpen(true)} />
        <KpiCard testId="kpi-nego-volume" label="Volume négocié actif" value={fxof(k.negotiationVolume)}
          sub={`${k.negotiationActiveCount} dossier${k.negotiationActiveCount > 1 ? 's' : ''} en cours`}
          icon={Zap} accent="violet" onClick={() => navigate('/admin/negotiations')} />
      </div>

      {/* ============== Sprint A Module 5 — Widget Conformité FNE (compact strip) ============== */}
      <FneComplianceWidget />

      {/* ============== KPI HERO STRIP — Row 2: Cash & Risk ============== */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-row-2">
        <KpiCard testId="kpi-dso" label="DSO" value={`${k.dso} j`}
          sub={`Encours ${fxof(k.totalOutstanding)}`} icon={Clock}
          accent={k.dso <= 45 ? 'emerald' : k.dso <= 60 ? 'amber' : 'rose'} alert={k.dso > 60} />
        <KpiCard testId="kpi-outstanding" label="Encours total" value={fxof(k.totalOutstanding)}
          sub="Créances clients ouvertes" icon={Wallet} accent="amber" />
        <KpiCard testId="kpi-cashin-30d" label="Trésorerie 30j" value={fxof(k.expectedCashIn30d)}
          sub="Encaissements prévus" icon={ArrowUpRight} accent="emerald" />
        <KpiCard testId="kpi-collected-30d" label="Encaissé (30j)" value={fxof(k.cashCollected30d)}
          sub="Cash réellement reçu" icon={CheckCircle2} accent="emerald" />
        <KpiCard testId="kpi-concentration" label="Concentration Top-3" value={`${k.concentrationTop3Pct}%`}
          sub={k.topClient ? `${k.topClient}: ${k.concentrationTop1Pct}%` : '—'}
          icon={Users}
          accent={k.concentrationTop1Pct > 50 ? 'rose' : k.concentrationTop3Pct > 70 ? 'amber' : 'emerald'}
          alert={k.concentrationTop1Pct > 50} />
        <KpiCard testId="kpi-backlog" label="Backlog facturation"
          value={k.billingBacklogAmount ? fxof(k.billingBacklogAmount) : k.billingBacklogCount}
          sub={k.billingBacklogAmount ? `${k.billingBacklogCount} op(s) livrée(s) non facturée(s)` : "Ops livrées non facturées"}
          icon={FileX}
          accent={k.billingBacklogCount > 5 ? 'rose' : k.billingBacklogCount > 0 ? 'amber' : 'emerald'}
          alert={k.billingBacklogCount > 5}
          onClick={() => navigate('/admin/audit-factures')} />
      </div>

      {/* ============== INSIGHTS NARRATIFS ============== */}
      {data.insights.length > 0 && (
        <div className="bg-gradient-to-r from-emerald-50/60 to-blue-50/40 border border-emerald-200/60 rounded-xl p-4" data-testid="insights-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#16A34A]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-800">Insights du jour</span>
          </div>
          <ul className="space-y-1.5">
            {data.insights.map((insight, i) => (
              <li key={i} className="text-sm text-zinc-700 flex items-start gap-2">
                <ChevronRight className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(insight.replace(/\*\*(.*?)\*\*/g, '<strong class="text-zinc-950">$1</strong>')) }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ============== CHARTS ROW ============== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Évolution CA 12 mois */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-4" data-testid="revenue-evolution-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-zinc-950">Évolution mensuelle CA</h3>
              <p className="text-xs text-zinc-500">Spot vs Contrat — 12 derniers mois</p>
            </div>
            <Badge variant="outline" className="text-[10px]">XOF</Badge>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.sparkline12m} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gSpot" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#16A34A" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#16A34A" stopOpacity={0.04} />
                </linearGradient>
                <linearGradient id="gContract" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#71717a' }} width={50} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }}
                formatter={(v) => fxof(v)}
              />
              <Area type="monotone" dataKey="contract" stackId="1" stroke="#3b82f6" fill="url(#gContract)" name="Contrat" />
              <Area type="monotone" dataKey="spot" stackId="1" stroke="#16A34A" fill="url(#gSpot)" name="Spot" />
              {budget?.monthly_revenue_target > 0 && (
                <ReferenceLine y={budget.monthly_revenue_target} stroke="#a855f7" strokeDasharray="4 4" label={{ value: 'Budget', fill: '#a855f7', fontSize: 10, position: 'right' }} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Cash flow 4 semaines */}
        <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="cashflow-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-zinc-950">Cash flow 4 semaines</h3>
              <p className="text-xs text-zinc-500">Encaissements prévus</p>
            </div>
            <Badge variant="outline" className="text-[10px]">Forecast</Badge>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.cashFlow4w} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="weekStart" tick={{ fontSize: 10, fill: '#71717a' }} />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: '#71717a' }} width={50} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }}
                formatter={(v) => fxof(v)}
              />
              <Bar dataKey="expectedIn" fill="#16A34A" name="Encaissements" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ============== IMPAYÉS VS PAYÉS (filtrable par période) ============== */}
      <PaidVsUnpaidChart />

      {/* ============== FNE GÉNÉRÉES VS BAOBAB ============== */}
      <FneCoverageChart />

      {/* ============== TOP CLIENTS ============== */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="top-clients-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-950">Top 5 clients</h3>
            <p className="text-xs text-zinc-500">Part dans le CA 12 mois glissants</p>
          </div>
          <PieIcon className="h-4 w-4 text-zinc-400" />
        </div>
        <div className="space-y-2">
          {data.topClients.length === 0 ? (
            <p className="text-sm text-zinc-400 italic">Aucune donnée</p>
          ) : data.topClients.map((c, i) => (
            <div key={c.name} className="flex items-center gap-3 group" data-testid={`top-client-${i}`}>
              <div className="h-7 w-7 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-700 shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-zinc-900 truncate">{c.name}</span>
                  <span className="text-sm tabular-nums font-semibold text-zinc-950">{fxof(c.revenue)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 bg-zinc-100 rounded-full flex-1 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${i === 0 && c.share > 50 ? 'bg-rose-400' : 'bg-[#16A34A]'}`}
                      style={{ width: `${Math.min(c.share, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs tabular-nums text-zinc-500 w-12 text-right">{c.share.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
        </TabsContent>

        <TabsContent value="fne-reg" className="mt-4">
          {activeTab === 'fne-reg' && <FneRegularizationTab />}
        </TabsContent>

        <TabsContent value="treasury" className="mt-4">

          {activeTab === 'treasury' && <TreasureGlobalePage embedded />}
        </TabsContent>

        {canSeeForecast && (
          <TabsContent value="forecast" className="mt-4">
            {activeTab === 'forecast' && <CashForecastTab />}
          </TabsContent>
        )}

        <TabsContent value="cashflow" className="mt-4">
          {activeTab === 'cashflow' && <CashflowTab />}
        </TabsContent>

        <TabsContent value="client-margin" className="mt-4">
          {activeTab === 'client-margin' && <ClientMarginTab />}
        </TabsContent>

        <TabsContent value="profitability" className="mt-4">
          {activeTab === 'profitability' && <ProfitabilityTab />}
        </TabsContent>
        <TabsContent value="retention" className="mt-4">
          {activeTab === 'retention' && <RetentionTab />}
        </TabsContent>
        <TabsContent value="dpo" className="mt-4">
          {activeTab === 'dpo' && <DpoCccTab />}
        </TabsContent>
        <TabsContent value="stress" className="mt-4">
          {activeTab === 'stress' && <StressTestTab topClient={k.topClient} />}
        </TabsContent>
        <TabsContent value="hidden-tax" className="mt-4">
          {activeTab === 'hidden-tax' && <HiddenTaxTab />}
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          {activeTab === 'audit' && <FinanceAuditPage />}
        </TabsContent>
      </Tabs>

      <BudgetDialog open={budgetOpen} onClose={() => setBudgetOpen(false)} onSave={saveBudget} budget={budget} />
      <EmailConfigDialog open={emailOpen} onClose={() => setEmailOpen(false)} />
      <EtatsFinanciersDialog open={etatsOpen} onClose={() => setEtatsOpen(false)} />
    </div>
  );
};

export default function FinanceCockpitPageGuarded() {
  return (
    <RequirePermission
      anyPermission={[PERMS.FINANCE_COCKPIT_VIEW, PERMS.FINANCE_COCKPIT_FULL, PERMS.FINANCE_DASHBOARD_VIEW, PERMS.FINANCE_KPIS_VIEW]}
    >
      <FinanceCockpitPage />
    </RequirePermission>
  );
}
